import React, { useMemo, useState } from 'react';
import { ListChecks, CheckSquare, MessageSquare, ChevronDown, ChevronUp, PieChart, BarChart3, Filter, CircleDot, Circle } from 'lucide-react';
import GraficoBarras from './GraficoBarras';
import GraficoPizza from './GraficoPizza';
import GraficoFunil from './GraficoFunil';
import GraficoBolhas from './GraficoBolhas';
import TextoTruncavel from './TextoTruncavel';
import { corPorIndice } from './paletaResultados';

const META_TIPO = {
  unica_escolha: { label: 'Única escolha', icon: ListChecks, cor: 'bg-blue-50 text-blue-700' },
  multipla_escolha: { label: 'Múltipla escolha', icon: CheckSquare, cor: 'bg-emerald-50 text-emerald-700' },
  aberta: { label: 'Texto aberto', icon: MessageSquare, cor: 'bg-amber-50 text-amber-700' }
};

// Opções de visualização por tipo de pergunta — o admin escolhe qual ver, a
// contagem de opções só decide o ponto de partida (padraoPor), não trava a
// escolha. Texto aberto só tem bolhas: nada de "nuvem de palavras" clássica
// (isso fica reservado pra tela pública — ver ResultadoEnqueteDinamica.js) e
// nada de barra pra frequência de palavra aqui dentro do gerenciador, que
// precisa de algo mais concreto — daí a pré-visualização de respostas reais
// no lugar da lista de palavras (ver RespostasPreview abaixo).
const OPCOES_VISUALIZACAO = {
  unica_escolha: [
    { id: 'rosca', label: 'Rosca', icon: CircleDot },
    { id: 'pizza', label: 'Pizza', icon: PieChart },
    { id: 'barra', label: 'Barra', icon: BarChart3 }
  ],
  multipla_escolha: [
    { id: 'funil', label: 'Funil', icon: Filter },
    { id: 'barra', label: 'Barra', icon: BarChart3 }
  ],
  aberta: [
    { id: 'bolhas', label: 'Bolhas', icon: Circle }
  ]
};

function padraoPor(pergunta) {
  if (pergunta.tipo === 'aberta') return 'bolhas';
  const qtd = (pergunta.itens || []).length;
  if (pergunta.tipo === 'unica_escolha') {
    if (qtd === 2) return 'rosca';
    if (qtd === 3) return 'pizza';
    return 'barra';
  }
  return qtd <= 3 ? 'funil' : 'barra';
}

// Grupo de botões de ícone pra trocar o tipo de gráfico — padrão comum em
// dashboard (Excel, ferramentas de BI): um controle pequeno e sempre visível
// acima do gráfico, não um menu escondido. Só faz sentido existir quando há
// mais de uma opção pra trocar.
const SeletorVisualizacao = ({ opcoes, valor, onMudar }) => {
  if (opcoes.length < 2) return null;

  return (
    <div className="inline-flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
      {opcoes.map((opcao) => {
        const Icone = opcao.icon;
        const ativo = opcao.id === valor;
        return (
          <button
            key={opcao.id}
            type="button"
            onClick={() => onMudar(opcao.id)}
            title={opcao.label}
            aria-pressed={ativo}
            className={`flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-md transition-colors ${
              ativo ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icone className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
};

// Coluna da esquerda pra única/múltipla escolha: valor exposto (opção →
// contagem → %), sempre visível ao lado do gráfico — leitura rápida sem
// depender de interpretar a forma visual.
const ListaValores = ({ itens, total, rotuloValor }) => {
  const ordenados = useMemo(() => [...(itens || [])].sort((a, b) => b.votos - a.votos), [itens]);

  if (ordenados.length === 0) {
    return <p className="text-sm text-gray-400 italic">Nenhum dado registrado ainda.</p>;
  }

  return (
    <ul className="space-y-1.5">
      {ordenados.map((item, indice) => {
        const percentual = total > 0 ? Math.round((item.votos / total) * 1000) / 10 : 0;
        return (
          <li key={item.slug} className="flex items-center gap-2.5 text-sm">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: corPorIndice(indice) }} />
            <span className="text-gray-700 flex-1 min-w-0 truncate">{item.label}</span>
            <span className="font-semibold text-gray-900 flex-shrink-0">{item.votos.toLocaleString('pt-BR')}</span>
            <span className="text-xs text-gray-400 flex-shrink-0 w-12 text-right">{percentual}%</span>
          </li>
        );
      })}
      <li className="text-xs text-gray-400 pt-1">Base: {total.toLocaleString('pt-BR')} {rotuloValor}</li>
    </ul>
  );
};

const PREVIA_MAXIMA = 5;

// Coluna da esquerda pra texto aberto: uma amostra de respostas reais (não
// palavras soltas) — o admin precisa ver o que as pessoas realmente
// escreveram, não só a frequência. A lista completa continua disponível no
// painel colapsável abaixo do card (RespostasTextuais).
const RespostasPreview = ({ respostas }) => {
  if (!respostas || respostas.length === 0) {
    return <p className="text-sm text-gray-400 italic">Nenhuma resposta registrada ainda.</p>;
  }

  const previa = respostas.slice(0, PREVIA_MAXIMA);

  return (
    <div>
      <ul className="space-y-2">
        {previa.map((texto, indice) => (
          <li key={indice} className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
            <TextoTruncavel texto={texto} />
          </li>
        ))}
      </ul>
      {respostas.length > PREVIA_MAXIMA && (
        <p className="text-xs text-gray-400 mt-2">+ {respostas.length - PREVIA_MAXIMA} resposta(s) — veja todas abaixo</p>
      )}
    </div>
  );
};

// Painel colapsável com todas as respostas de texto cru de uma pergunta
// aberta — lista completa (a pré-visualização ao lado do gráfico só mostra
// as primeiras). Cada resposta usa o mesmo truncamento com "Ver mais", pra
// texto longo não estourar a lista.
const RespostasTextuais = ({ respostas }) => {
  const [aberto, setAberto] = useState(false);

  if (!respostas || respostas.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
      >
        <span>Ver todas as respostas ({respostas.length})</span>
        {aberto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {aberto && (
        <ul className="mt-3 space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {respostas.map((texto, indice) => (
            <li key={indice} className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
              <TextoTruncavel texto={texto} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Uma pergunta normalizada (ver resultadosService.js) vira um card com duas
// colunas: valores expostos (sempre visível) + gráfico (forma trocável pelo
// seletor). O tipo/quantidade de opções só decide a forma inicial.
const CardPerguntaResultado = ({ pergunta }) => {
  const meta = META_TIPO[pergunta.tipo] || META_TIPO.unica_escolha;
  const Icone = meta.icon;
  const opcoesVisualizacao = OPCOES_VISUALIZACAO[pergunta.tipo] || OPCOES_VISUALIZACAO.unica_escolha;
  const [visualizacao, setVisualizacao] = useState(() => padraoPor(pergunta));

  let grafico;
  if (visualizacao === 'rosca') grafico = <GraficoPizza itens={pergunta.itens} totalRespostas={pergunta.totalRespostas} variante="rosca" />;
  else if (visualizacao === 'pizza') grafico = <GraficoPizza itens={pergunta.itens} totalRespostas={pergunta.totalRespostas} variante="pizza" />;
  else if (visualizacao === 'funil') grafico = <GraficoFunil itens={pergunta.itens} totalRespostas={pergunta.totalRespostas} />;
  else if (visualizacao === 'bolhas') grafico = <GraficoBolhas palavras={pergunta.nuvemPalavras} />;
  else grafico = <GraficoBarras itens={pergunta.itens} totalRespostas={pergunta.totalRespostas} orientacao="vertical" />;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900 leading-snug flex-1 min-w-0">{pergunta.texto}</h3>
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${meta.cor}`}>
          <Icone className="w-3 h-3" /> {meta.label}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="lg:border-r lg:border-gray-100 lg:pr-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">
            {pergunta.tipo === 'aberta' ? 'Respostas' : 'Valores'}
          </p>
          {pergunta.tipo === 'aberta'
            ? <RespostasPreview respostas={pergunta.respostas} />
            : <ListaValores itens={pergunta.itens} total={pergunta.totalRespostas} rotuloValor="respondente(s)" />}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Gráfico</p>
            <SeletorVisualizacao opcoes={opcoesVisualizacao} valor={visualizacao} onMudar={setVisualizacao} />
          </div>
          {grafico}
        </div>
      </div>

      {pergunta.tipo === 'aberta' && <RespostasTextuais respostas={pergunta.respostas} />}
    </div>
  );
};

export default CardPerguntaResultado;
